import * as React from "react";
import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import {
  createBrowserRouter,
  RouterProvider,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";
import { Helmet } from "react-helmet";
import ErrorBoundary from "./utils/ErrorBoundary";
import GlobalAppContext from "./utils/GlobalAppContext";
import { getPageProps } from "./utils/utils";
import getRoutes from "./routes/routes";
import queryClient from "./utils/QueryClient";
import ReactQueryDevtool from "./utils/ReactQueryDevTools";

document.addEventListener("DOMContentLoaded", async () => {
  const {
    domContainer,
    globalAppContext,
    initialAlerts,
    sentryProps,
  } = await getPageProps();
  const { sentry_dsn, sentry_traces_sample_rate } = sentryProps;
  const { currentUser } = globalAppContext;

  if (sentry_dsn) {
    Sentry.init({
      dsn: sentry_dsn,
      integrations: [
        Sentry.reactRouterV7BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      ],
      tracesSampleRate: sentry_traces_sample_rate,
    });

    if (currentUser?.name) {
      Sentry.setUser({ username: currentUser.name });
    }
  }

  const brainzPlayerDisabled =
    globalAppContext?.userPreferences?.brainzplayer?.brainzplayerEnabled ===
    false;

  const routes = getRoutes(
    currentUser?.name,
    !brainzPlayerDisabled,
    initialAlerts
  );
  const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(
    createBrowserRouter
  );
  const router = sentryCreateBrowserRouter(routes);

  const renderRoot = createRoot(domContainer!);
  renderRoot.render(
    <ErrorBoundary>
      <GlobalAppContext.Provider value={globalAppContext}>
        <Helmet defaultTitle="ListenBrainz" titleTemplate="%s - ListenBrainz" />
        <ReactQueryDevtool client={queryClient}>
          <RouterProvider router={router} />
        </ReactQueryDevtool>
      </GlobalAppContext.Provider>
    </ErrorBoundary>
  );
});
